import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  Transition,
} from '@headlessui/react';
import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import GenericModal from '../../../../ui/GenericModal';
import { RaidrV2RoutePaths } from '../../../../../routes/page-routes';
import { getKidOverviewHistoricalData } from '../../../../../api/kids/kids.routes';
import { getReviewVersionParams } from '../utils/reviewNavigation';
import type { KidOverviewDataItem } from '../../../../../api/kids/types';
import { KidDocumentStatus } from '../types/KidEditorV2.types';
import { useFundAndShareClassName } from '../hooks/useFundAndShareClassName';
import { FiChevronDown, FiCheck } from 'react-icons/fi';
import { Button } from '@mui/material';
import { KID_STATUS_COLOR } from '../../../../../utilities/colorUtilities';

interface ReviewMoreDialogV2Props {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  clientId: string;
  overviewData: KidOverviewDataItem[];
  currentFundId: string;
  currentShareClassId: string;
  currentDocumentLanguage: string;
  onReturnToOverview: () => void;
}

interface ReviewFundGroup {
  fund_id: string;
  items: KidOverviewDataItem[];
}

const ReviewDocumentLabel: FC<{
  clientId: string;
  fundId: string;
  shareClassId: string;
}> = ({ clientId, fundId, shareClassId }) => {
  const { fundNameFull, shareClassName, isLoading } = useFundAndShareClassName({
    clientId,
    fundId,
    shareClassId,
    pageKey: 'kid-review-v2',
  });

  const displayFundName = fundNameFull || (isLoading ? '…' : fundId);
  const displayShareClassName = shareClassName || (isLoading ? '…' : shareClassId);

  return (
    <span className="text-sm text-gray-900">
      {displayFundName} – {displayShareClassName}
    </span>
  );
};

const FundNameLabel: FC<{
  clientId: string;
  fundId: string;
  shareClassId: string;
}> = ({ clientId, fundId, shareClassId }) => {
  const { fundNameFull, isLoading } = useFundAndShareClassName({
    clientId,
    fundId,
    shareClassId,
    pageKey: 'kid-review-v2',
  });

  return (
    <span className="block truncate">
      {fundNameFull || (isLoading ? '…' : fundId)}
    </span>
  );
};

const ReviewMoreDialogV2: FC<ReviewMoreDialogV2Props> = ({
  isOpen,
  setIsOpen,
  clientId,
  overviewData,
  currentFundId,
  currentShareClassId,
  currentDocumentLanguage,
  onReturnToOverview,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const reviewableItems = useMemo(
    () =>
      overviewData.filter(
        (item) =>
          item.status === KidDocumentStatus.Edited &&
          !(
            item.fund_id === currentFundId &&
            item.share_class_id === currentShareClassId &&
            item.document_language === currentDocumentLanguage
          ),
      ),
    [overviewData, currentFundId, currentShareClassId, currentDocumentLanguage],
  );

  const fundsForUI = useMemo<ReviewFundGroup[]>(() => {
    const fundMap = new Map<string, ReviewFundGroup>();

    reviewableItems.forEach((item) => {
      if (!fundMap.has(item.fund_id)) {
        fundMap.set(item.fund_id, {
          fund_id: item.fund_id,
          items: [],
        });
      }
      fundMap.get(item.fund_id)!.items.push(item);
    });

    return Array.from(fundMap.values());
  }, [reviewableItems]);

  const [selectedFund, setSelectedFund] = useState<ReviewFundGroup | null>(null);
  const [selectedItem, setSelectedItem] = useState<KidOverviewDataItem | null>(null);

  // Initialise selection when reviewable items change
  useEffect(() => {
    if (fundsForUI.length === 0) {
      setSelectedFund(null);
      setSelectedItem(null);
      return;
    }

    // Prefer the current fund if it still has reviewable items
    const fundByCurrent = fundsForUI.find((f) => f.fund_id === currentFundId);
    const initialFund = fundByCurrent ?? fundsForUI[0];
    const initialItem = initialFund.items[0] ?? null;

    setSelectedFund(initialFund);
    setSelectedItem(initialItem);
  }, [fundsForUI, currentFundId]);

  const handleReviewSelected = async () => {
    if (!selectedItem) return;

    setIsOpen(false);

    const historicalData = await queryClient.fetchQuery({
      queryKey: [
        'kid-historical-overview',
        clientId,
        selectedItem.fund_id,
        selectedItem.share_class_id,
        selectedItem.document_language,
      ],
      queryFn: () =>
        getKidOverviewHistoricalData(
          clientId,
          selectedItem.fund_id,
          selectedItem.share_class_id,
          selectedItem.document_language,
        ),
    });

    const params = getReviewVersionParams(historicalData);
    const search = new URLSearchParams({
      fundId: selectedItem.fund_id,
      shareClassId: selectedItem.share_class_id,
      documentLanguage: selectedItem.document_language,
    });

    if (params) {
      search.set('targetVersion', params.targetVersion);
      search.set('compareVersion', params.compareVersion);
    }

    navigate(`${RaidrV2RoutePaths.KID_REVIEW_V2}?${search.toString()}`);
  };

  const disableReview = !selectedFund || !selectedItem;

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Review complete"
      description="Would you like to review another document? Select a fund and share class below to continue."
      className="max-w-3xl"
    >
      {reviewableItems.length === 0 ? (
        <p className="py-4 text-center text-sm text-gray-500">
          There are no other documents currently ready for review.
        </p>
      ) : (
        <div className="space-y-6">
          <div className="space-y-6">
            <div>
              <Listbox
                value={selectedFund ?? undefined}
                by="fund_id"
                onChange={(fund: ReviewFundGroup) => {
                  setSelectedFund(fund);
                  setSelectedItem(fund.items[0] ?? null);
                }}
              >
                <div className="relative">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Select Fund
                  </label>
                  <ListboxButton
                    className={`relative w-full ${fundsForUI.length === 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                      } rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:outline-none focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="block truncate">
                        {selectedFund ? (
                          <FundNameLabel
                            clientId={clientId}
                            fundId={selectedFund.fund_id}
                            shareClassId={selectedFund.items[0]?.share_class_id}
                          />
                        ) : (
                          'Select a fund'
                        )}
                      </span>
                      <span className="inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {selectedFund ? selectedFund.items.length : reviewableItems.length} ready
                        for review
                      </span>
                    </div>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <FiChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </ListboxButton>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions className="absolute z-[60] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {fundsForUI.map((fund) => (
                        <ListboxOption
                          key={fund.fund_id}
                          value={fund}
                          className="relative cursor-pointer select-none py-2 pl-10 pr-4 text-gray-900 data-[focus]:!bg-blue-100 data-[selected]:bg-blue-50 data-[focus]:text-blue-900"
                        >
                          {({ selected }) => (
                            <>
                              <div className="flex items-center justify-between gap-2">
                                <span className={selected ? 'font-medium' : 'font-normal'}>
                                  <FundNameLabel
                                    clientId={clientId}
                                    fundId={fund.fund_id}
                                    shareClassId={fund.items[0]?.share_class_id}
                                  />
                                </span>
                                <span className="inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                                  {fund.items.length} ready
                                </span>
                              </div>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                  <FiCheck className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </div>
              </Listbox>
            </div>

            <div>
              <Listbox
                value={selectedItem ?? undefined}
                onChange={(item: KidOverviewDataItem) => setSelectedItem(item)}
                disabled={!selectedFund}
              >
                <div className="relative">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Select Share Class
                  </label>
                  <ListboxButton
                    className={`relative w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:outline-none focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500 ${!selectedFund ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                      }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="block truncate">
                        {selectedItem ? (
                          <ReviewDocumentLabel
                            clientId={clientId}
                            fundId={selectedItem.fund_id}
                            shareClassId={selectedItem.share_class_id}
                          />
                        ) : (
                          'Select a share class'
                        )}
                      </span>
                    </div>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <FiChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </ListboxButton>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions className="absolute z-[60] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {(selectedFund?.items ?? []).map((item) => (
                        <ListboxOption
                          key={`${item.fund_id}-${item.share_class_id}-${item.document_language}`}
                          value={item}
                          className="relative cursor-pointer select-none py-2 pl-10 pr-4 text-gray-900 data-[focus]:!bg-blue-100 data-[selected]:bg-blue-50 data-[focus]:!text-blue-900"
                        >
                          {({ selected }) => (
                            <>
                              <div className="flex flex-col gap-0.5">
                                <ReviewDocumentLabel
                                  clientId={clientId}
                                  fundId={item.fund_id}
                                  shareClassId={item.share_class_id}
                                />
                                <span className="text-xs text-gray-500">
                                  Language: {item.document_language}
                                </span>
                              </div>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                  <FiCheck className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </div>
              </Listbox>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="contained"
              disableElevation
              size="small"
              className="normal-case !bg-gray-200 !text-gray-700 hover:!bg-gray-300"
              onClick={() => {
                setIsOpen(false);
                onReturnToOverview();
              }}
            >
              <span className="text-xs">Return to overview</span>
            </Button>
            <Button
              variant="contained"
              disableElevation
              size="small"
              className="normal-case !text-white hover:brightness-90 disabled:!opacity-40"
              style={{ backgroundColor: KID_STATUS_COLOR[KidDocumentStatus.Edited] }}
              disabled={disableReview}
              onClick={handleReviewSelected}
            >
              <span className="text-xs">Review selected document</span>
            </Button>
          </div>
        </div>
      )}
    </GenericModal>
  );
};

export default ReviewMoreDialogV2;

